/* biome-ignore-all lint/suspicious/noExplicitAny: legacy data structure */
import React, { useEffect, useState } from "react"
import { Modal, Form, Switch, Typography, message, Tag, Avatar } from "antd"
import { UserOutlined, ExperimentOutlined, LockOutlined, CheckCircleOutlined } from "@ant-design/icons"
import { userService } from "@/services/user/userApi"

const { Text } = Typography

interface UserEditModalProps {
  open: boolean
  user: any | null
  onClose: () => void
  onSuccess: () => void
}

export const UserEditModal: React.FC<UserEditModalProps> = ({
  open,
  user,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm()
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open && user) {
      form.setFieldsValue({
        is_test: user.is_test === 1,
        is_active: user.is_active === 1,
      })
    }
  }, [open, user, form])

  const handleSubmit = async () => {
    if (!user) return
    try {
      const values = await form.validateFields()
      setSaving(true)

      const payload = {
        is_test: values.is_test ? 1 : 0,
      }

      const res = await userService.update(user.id, payload)
      if (res.code === 200) {
        message.success("Cập nhật thông tin người dùng thành công")
        onSuccess()
        onClose()
      } else {
        message.error(res.message || "Cập nhật thất bại")
      }
    } catch (error: any) {
      message.error(error.message || "Đã xảy ra lỗi khi lưu thông tin")
    } finally {
      setSaving(false)
    }
  }

  if (!user) return null

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <span>Chỉnh sửa tài khoản người dùng</span>
          <span className="text-xs font-normal text-gray-400">#{user.id}</span>
        </div>
      }
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText="Lưu thay đổi"
      cancelText="Hủy"
      confirmLoading={saving}
      width={480}
      destroyOnClose
    >
      <div className="my-3">
        {/* User Summary Card */}
        <div className="flex items-center gap-3 p-3 mb-4 rounded-lg bg-gray-50 border border-gray-100">
          <Avatar
            size={42}
            src={user.avatar}
            icon={<UserOutlined />}
            className="bg-primary/20 text-primary shrink-0"
          />
          <div className="flex flex-col min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-800 text-sm truncate">
                {user.name || user.username}
              </span>
              {user.is_test === 1 && (
                <Tag color="warning" className="m-0 text-[10px] font-semibold">
                  🧪 Test
                </Tag>
              )}
            </div>
            <Text type="secondary" className="text-xs truncate">
              {user.email} (@{user.username})
            </Text>
          </div>
        </div>

        {/* Form Controls */}
        <Form form={form} layout="vertical">
          <div className="flex items-start justify-between gap-4 p-3 rounded-lg border border-amber-100 bg-amber-50/40 mb-3">
            <div className="flex flex-col">
              <span className="font-medium text-gray-800 flex items-center gap-1.5 text-sm">
                <ExperimentOutlined className="text-amber-600" />
                Tài khoản Test (Kiểm thử)
              </span>
              <Text type="secondary" className="text-xs mt-0.5 text-gray-500">
                Khi bật, tài khoản này sẽ được <strong>loại trừ hoàn toàn</strong> khỏi Bảng xếp hạng và các thống kê thực tế.
              </Text>
            </div>
            <Form.Item name="is_test" valuePropName="checked" className="m-0 shrink-0">
              <Switch checkedChildren="Test" unCheckedChildren="Thật" />
            </Form.Item>
          </div>

          <div className="flex items-start justify-between gap-4 p-3 rounded-lg border border-gray-200 bg-gray-50/60 opacity-60">
            <div className="flex flex-col">
              <span className="font-medium text-gray-600 flex items-center gap-1.5 text-sm">
                <CheckCircleOutlined className="text-gray-400" />
                Trạng thái hoạt động <Tag className="m-0 text-[10px]">Tạm khóa</Tag>
              </span>
              <Text type="secondary" className="text-xs mt-0.5 text-gray-400">
                Tính năng khóa/mở tài khoản đang tạm thời vô hiệu hóa.
              </Text>
            </div>
            <Form.Item name="is_active" valuePropName="checked" className="m-0 shrink-0">
              <Switch disabled checkedChildren="Mở" unCheckedChildren="Khóa" />
            </Form.Item>
          </div>
        </Form>
      </div>
    </Modal>
  )
}

export default UserEditModal
